import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';

const Reports = () => {
    const { authFetch, depos, systemSettings } = useContext(AppContext);
    const { t } = useTranslation();
    const tvaRate = parseFloat(systemSettings?.default_tva || 19);
    
    const [reportType, setReportType] = useState('monthly'); // 'monthly' or 'intake'
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)).toISOString().slice(0, 10)); // First of month
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10)); // Today
    const [selectedDepo, setSelectedDepo] = useState('all');
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch report data when filters change
    useEffect(() => {
        setLoading(true);
        authFetch(`/api/reports_advanced?type=${reportType}&start=${startDate}&end=${endDate}&depo_id=${selectedDepo}`)
            .then(res => res.json())
            .then(data => {
                setReportData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [reportType, startDate, endDate, selectedDepo, authFetch]);

    const handlePrint = () => {
        const branchLabel = selectedDepo === 'all' ? 'All Branches' : (depos.find(d => d.id == selectedDepo)?.name || selectedDepo);
        const typeLabel = reportType === 'monthly' ? 'Monthly Summary & Margins' : 'Product Intake Details';

        let bodyHtml = '';
        if (reportData && reportType === 'monthly' && reportData.executive_summary) {
            const s = reportData.executive_summary;
            const phRows = (reportData.price_history || []).map((ph, i) =>
                `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;font-weight:700">${ph.product_name}</td>
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:right;color:#6b7280;text-decoration:line-through">${ph.old_price}</td>
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:700">${ph.new_price}</td>
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:right">
                        <span style="background:${ph.new_price>ph.old_price?'#dcfce7':'#fee2e2'};color:${ph.new_price>ph.old_price?'#15803d':'#dc2626'};border-radius:4px;padding:2px 6px">
                            ${ph.new_price>ph.old_price?'&#8593;':'&#8595;'} ${ph.percentage_change}%
                        </span>
                    </td>
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;color:#9ca3af">${new Date(ph.changed_at).toLocaleString()}</td>
                    <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">${ph.username}</td>
                </tr>`).join('');
            bodyHtml = `
                <div style="display:flex;gap:16px;margin-bottom:20px">
                    <div style="flex:1;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:16px">
                        <div style="font-size:10px;font-weight:900;text-transform:uppercase;color:#065f46;margin-bottom:4px">Total Intake Value (HT)</div>
                        <div style="font-size:22px;font-weight:900;color:#059669">${(s.total_intake_value_ht||0).toLocaleString()} CFA</div>
                    </div>
                    <div style="flex:1;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:16px">
                        <div style="font-size:10px;font-weight:900;text-transform:uppercase;color:#991b1b;margin-bottom:4px">Total Removal Value (HT)</div>
                        <div style="font-size:22px;font-weight:900;color:#dc2626">${(s.total_removal_value_ht||0).toLocaleString()} CFA</div>
                    </div>
                </div>
                ${reportData.narrative ? `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;margin-bottom:20px;font-style:italic;color:#475569">"${reportData.narrative}"</div>` : ''}
                ${phRows ? `<div className="w-full overflow-x-auto max-w-full">
<table style="width:100%;border-collapse:collapse;font-size:10px">
                    <thead><tr style="background:#f1f5f9">
                        <th style="padding:8px;text-align:left;border-bottom:1px solid #e2e8f0">Product</th>
                        <th style="padding:8px;text-align:right;border-bottom:1px solid #e2e8f0">Old Price</th>
                        <th style="padding:8px;text-align:right;border-bottom:1px solid #e2e8f0">New Price</th>
                        <th style="padding:8px;text-align:right;border-bottom:1px solid #e2e8f0">Change</th>
                        <th style="padding:8px;text-align:left;border-bottom:1px solid #e2e8f0">Date</th>
                        <th style="padding:8px;text-align:left;border-bottom:1px solid #e2e8f0">By</th>
                    </tr></thead><tbody>${phRows}</tbody></table>
</div>` : '<p style="color:#9ca3af;font-style:italic">No price changes in this period.</p>'}`;
        } else if (reportData && reportData.data) {
            const tots = reportData.totals || {};
            const dataRows = (reportData.data||[]).map((r,i) =>
                `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;font-size:9px;color:#9ca3af">${new Date(r.created_at).toLocaleString()}</td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;font-weight:700;color:${r.movement_type==='Addition'?'#059669':r.movement_type==='Removal'?'#dc2626':'#2563eb'}">${r.movement_type}</td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;font-weight:700">${r.product_name}<br><span style="font-size:9px;color:#9ca3af">${r.sku||''}</span></td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">${r.branch_name}</td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:900">${r.movement_type==='Removal'?'-':'+'}${r.quantity_added}</td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;text-align:right">${parseFloat(r.cost_ht||0).toFixed(2)}</td>
                    <td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:900">${parseFloat(r.total_line_cost_ttc||0).toFixed(2)} CFA</td>
                </tr>`).join('');
            bodyHtml = `
                <div style="display:flex;gap:12px;margin-bottom:20px">
                    <div style="flex:1;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:14px">
                        <div style="font-size:10px;font-weight:900;color:#6b7280;text-transform:uppercase;margin-bottom:4px">Total HT</div>
                        <div style="font-size:20px;font-weight:900">${(tots.grand_total_ht||0).toLocaleString()}</div>
                    </div>
                    <div style="flex:1;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:14px">
                        <div style="font-size:10px;font-weight:900;color:#6b7280;text-transform:uppercase;margin-bottom:4px">VAT (${tvaRate}%)</div>
                        <div style="font-size:20px;font-weight:900;color:#ef4444">+${(tots.vat_total||0).toLocaleString()}</div>
                    </div>
                    <div style="flex:1;background:#eef2ff;border:1px solid #c7d2fe;border-radius:8px;padding:14px">
                        <div style="font-size:10px;font-weight:900;color:#4338ca;text-transform:uppercase;margin-bottom:4px">Grand Total TTC</div>
                        <div style="font-size:20px;font-weight:900;color:#4338ca">${(tots.grand_total_ttc||0).toLocaleString()} CFA</div>
                    </div>
                </div>
                <div className="w-full overflow-x-auto max-w-full">
<table style="width:100%;border-collapse:collapse;font-size:10px">
                    <thead><tr style="background:#f1f5f9">
                        <th style="padding:8px;text-align:left;border-bottom:2px solid #e2e8f0">Date</th>
                        <th style="padding:8px;text-align:left;border-bottom:2px solid #e2e8f0">Type</th>
                        <th style="padding:8px;text-align:left;border-bottom:2px solid #e2e8f0">Product</th>
                        <th style="padding:8px;text-align:left;border-bottom:2px solid #e2e8f0">Branch</th>
                        <th style="padding:8px;text-align:right;border-bottom:2px solid #e2e8f0">Qty</th>
                        <th style="padding:8px;text-align:right;border-bottom:2px solid #e2e8f0">Cost HT</th>
                        <th style="padding:8px;text-align:right;border-bottom:2px solid #e2e8f0">Total TTC</th>
                    </tr></thead><tbody>${dataRows}</tbody>
                </table>
</div>`;
        } else {
            bodyHtml = '<p style="color:#9ca3af;font-style:italic;text-align:center;padding:40px">No data loaded.</p>';
        }

        const sigBoxes = ['Prepared By','Verified By','Authorized Signature']
            .map(l => `<div style="text-align:center;width:28%"><div style="border-bottom:1px solid #334155;height:32px;margin-bottom:4px"></div><div style="font-size:9px;color:#64748b;font-weight:700">${l}</div></div>`)
            .join('');

        const html = `<!DOCTYPE html><html><head>
            <meta charset="utf-8"><title>ERP Financial Report</title>
            <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; } @page { size: A4 portrait; margin: 10mm; }</style>
        </head><body>
            <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1e293b;padding-bottom:10px;margin-bottom:16px">
                <div>
                    <div style="font-size:18px;font-weight:900;text-transform:uppercase">ERP FINANCIAL REPORT</div>
                    <div style="font-size:11px;color:#475569;margin-top:2px;text-transform:uppercase;font-weight:700">${typeLabel}</div>
                </div>
                <div style="text-align:right;font-size:10px;color:#6b7280">
                    <div>Period: <b>${startDate} &#8594; ${endDate}</b></div>
                    <div>Branch: <b>${branchLabel}</b></div>
                    <div>Printed: <b>${new Date().toLocaleString()}</b></div>
                </div>
            </div>
            ${bodyHtml}
            <div style="margin-top:40px;border-top:1px solid #e2e8f0;padding-top:12px;display:flex;justify-content:space-between">${sigBoxes}</div>
            <div style="margin-top:12px;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8">
                <span>Generated by ERP System &#183; ${new Date().toLocaleString()}</span>
                <span>CONFIDENTIAL &#8212; Internal Use Only</span>
            </div>
        <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
        </body></html>`;

        const popup = window.open('', '_blank', 'width=950,height=750,scrollbars=yes');
        if (popup) { popup.document.write(html); popup.document.close(); }
        else alert('Please allow popups for this site to print.');
    };

    const renderMonthlySummary = () => {
        if (!reportData || !reportData.executive_summary) return null;
        const { executive_summary, narrative, price_history } = reportData;
        
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-100 shadow-sm">
                        <h3 className="text-emerald-800 font-bold uppercase text-xs tracking-wider mb-2">{t('Total Intake Value (HT)')}</h3>
                        <p className="text-3xl font-black text-emerald-600">{executive_summary.total_intake_value_ht.toLocaleString()} <span className="text-lg">CFA</span></p>
                    </div>
                    <div className="bg-red-50 p-6 rounded-xl border border-red-100 shadow-sm">
                        <h3 className="text-red-800 font-bold uppercase text-xs tracking-wider mb-2">{t('Total Removal Value (HT)')}</h3>
                        <p className="text-3xl font-black text-red-600">{executive_summary.total_removal_value_ht.toLocaleString()} <span className="text-lg">CFA</span></p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-bold text-gray-800 mb-2">{t('Executive Summary')}</h3>
                    <p className="text-gray-600 italic">"{narrative}"</p>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <h3 className="font-bold text-gray-800 mb-4">{t('Price Change History (Gains/Losses)')}</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="p-3">{t('Product')}</th>
                                    <th className="p-3">{t('Old Price')}</th>
                                    <th className="p-3">{t('New Price')}</th>
                                    <th className="p-3">{t('Change %')}</th>
                                    <th className="p-3">{t('Date')}</th>
                                    <th className="p-3">{t('By')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {price_history && price_history.length > 0 ? price_history.map((ph, i) => (
                                    <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                                        <td className="p-3 font-bold text-gray-800">{ph.product_name}</td>
                                        <td className="p-3 text-gray-500 line-through">{ph.old_price}</td>
                                        <td className="p-3 font-bold">{ph.new_price}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${ph.new_price > ph.old_price ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                {ph.new_price > ph.old_price ? '↑ ' : '↓ '}
                                                {ph.percentage_change}%
                                            </span>
                                        </td>
                                        <td className="p-3 text-gray-500">{new Date(ph.changed_at).toLocaleString()}</td>
                                        <td className="p-3">{ph.username}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="6" className="p-4 text-center text-gray-400 italic">{t('No price changes in this period')}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderIntakeReport = () => {
        if (!reportData || !reportData.data) return null;
        
        const cols = [
            { header: t('Date'), accessor: 'created_at', render: r => new Date(r.created_at).toLocaleString() },
            { header: t('Type'), accessor: 'movement_type', render: r => <span className={`font-bold ${r.movement_type === 'Addition' ? 'text-emerald-600' : (r.movement_type === 'Removal' ? 'text-red-600' : 'text-blue-600')}`}>{t(r.movement_type)}</span> },
            { header: t('Product'), accessor: 'product_name', render: r => <div><span className="font-bold block">{r.product_name}</span><span className="text-[10px] text-gray-400">{r.sku}</span></div> },
            { header: t('Depo'), accessor: 'branch_name' },
            { header: t('Qty Added'), accessor: 'quantity_added', render: r => <span className="font-bold text-lg">{r.movement_type === 'Removal' ? '-' : '+'}{r.quantity_added}</span> },
            { header: t('Cost HT'), accessor: 'cost_ht', render: r => parseFloat(r.cost_ht).toFixed(2) },
            { header: `${t('Total Line TTC')} (${tvaRate}% VAT)`, accessor: 'total_line_cost_ttc', render: r => <span className="font-black text-gray-900">{parseFloat(r.total_line_cost_ttc).toFixed(2)} CFA</span> }
        ];

        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <h4 className="text-gray-500 text-xs font-bold uppercase mb-1">{t('Total HT')}</h4>
                        <p className="text-2xl font-black">{reportData.totals.grand_total_ht.toLocaleString()}</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <h4 className="text-gray-500 text-xs font-bold uppercase mb-1">{t('VAT Total')} ({tvaRate}%)</h4>
                        <p className="text-2xl font-black text-red-500">+{reportData.totals.vat_total.toLocaleString()}</p>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                        <h4 className="text-indigo-800 text-xs font-bold uppercase mb-1">{t('Grand Total TTC')}</h4>
                        <p className="text-3xl font-black text-indigo-700">{reportData.totals.grand_total_ttc.toLocaleString()} CFA</p>
                    </div>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                     <DataTable columns={cols} data={reportData.data} />
                </div>
            </div>
        );
    };

    return (
        <div className="pb-12">
            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        .print-area, .print-area * { visibility: visible; }
                        .print-area { position: absolute; left: 0; top: 0; width: 100%; }
                        .no-print { display: none !important; }
                    }
                `}
            </style>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 no-print">
                <div>
                    <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">{t('Advanced Reporting')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Financial analysis, intakes, and margins')}</p>
                </div>
                
                <div className="flex flex-wrap gap-2 items-center bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
                    <select value={reportType} onChange={e => setReportType(e.target.value)} className="border-r border-gray-200 bg-transparent px-3 py-2 outline-none font-bold text-indigo-700 rounded-l-xl">
                        <option value="monthly">{t('Monthly Summary & Margins')}</option>
                        <option value="intake">{t('Product Intake Details')}</option>
                    </select>

                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent px-2 py-2 outline-none text-sm text-gray-600" />
                    <span className="text-gray-400 font-black">→</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent px-2 py-2 outline-none text-sm border-r border-gray-200 text-gray-600" />
                    
                    <select value={selectedDepo} onChange={e => setSelectedDepo(e.target.value)} className="bg-transparent px-3 py-2 outline-none text-sm font-bold text-gray-700 rounded-r-xl">
                        <option value="all">{t('All Branches')}</option>
                        {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </div>

                <button onClick={handlePrint} className="bg-gray-900 text-white px-6 py-3 rounded-xl font-black uppercase text-sm shadow flex items-center gap-2 hover:bg-black transition">
                    🖨️ {t('Print Report')}
                </button>
            </div>

            <div className="print-area">
                {/* Print Header */}
                <div className="hidden print:block mb-8 text-center border-b-2 border-gray-900 pb-6">
                    <h1 className="text-4xl font-black uppercase tracking-widest">{t('ERP FINANCIAL REPORT')}</h1>
                    <p className="text-xl mt-2 text-gray-600 uppercase font-bold">{reportType === 'monthly' ? t('Monthly Summary') : t('Product Intake Details')}</p>
                    <div className="mt-4 flex justify-center gap-8 text-sm">
                        <p><b>{t('Period')}:</b> {startDate} {t('to')} {endDate}</p>
                        <p><b>{t('Branch')}:</b> {selectedDepo === 'all' ? t('Consolidated') : depos.find(d => d.id == selectedDepo)?.name}</p>
                    </div>
                </div>

                {loading ? (
                    <div className="py-20 text-center animate-pulse"><p className="text-xl font-bold text-gray-400 uppercase tracking-widest">{t('Computing Reports...')}</p></div>
                ) : (
                    reportType === 'monthly' ? renderMonthlySummary() : renderIntakeReport()
                )}

                {/* Print Footer */}
                <div className="hidden print:flex mt-16 justify-between items-end">
                    <div className="text-xs text-gray-400">
                        {t('Generated automatically by ERP System')} • {new Date().toLocaleString()}
                    </div>
                    <div className="w-64 text-center">
                        <div className="border-b-2 border-gray-900 pb-8 mb-2"></div>
                        <p className="font-bold uppercase text-xs tracking-widest">{t('Authorized Signature')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Reports;
